import React, { useState, useEffect, useMemo, useRef } from "react";
import { UnifiedDateTimePicker } from "../..";

export interface DenmarkEditScheduleSectionProps {
  selectedChip?: number | null;
  selectedArticle: any[];
  currentArticleByChip: any | undefined;
  fallbackScheduleType: string;
  currentMediaDetailsForEdit: any;
  disabled?: boolean;
  onUpdateArticleByChip: (updates: Record<string, any>) => void;
}

const isEpochTimestamp = (v: number | undefined) =>
  v != null && v > 1000000000000;

const DenmarkEditScheduleSection: React.FC<DenmarkEditScheduleSectionProps> = ({
  selectedChip,
  selectedArticle,
  currentArticleByChip,
  fallbackScheduleType,
  currentMediaDetailsForEdit,
  disabled,
  onUpdateArticleByChip,
}) => {
  const [scheduleSectionUserEnabled, setScheduleSectionUserEnabled] =
    useState(false);

  const socialMatchForActiveChip = useMemo(() => {
    const socials = currentMediaDetailsForEdit?.socials ?? [];
    if (selectedChip == null) return null;
    return (
      socials.find(
        (s: any) =>
          (s?.job_type === "denmark" || s?.job_type === "denmark_embed") &&
          Number(s?.section ?? s?.id) === Number(selectedChip)
      ) ?? null
    );
  }, [currentMediaDetailsForEdit?.socials, selectedChip]);

  const isPublishedDenmarkChip =
    typeof currentArticleByChip?.status === "number" &&
    currentArticleByChip.status !== 0;

  const rawSavedScheduleMs = useMemo(() => {
    const raw =
      currentArticleByChip?.schedule ?? socialMatchForActiveChip?.schedule;
    if (raw == null || raw === 0 || Number(raw) === 0) return undefined;
    const n = Number(raw);
    return isEpochTimestamp(n) ? n : undefined;
  }, [currentArticleByChip?.schedule, socialMatchForActiveChip?.schedule]);

  const hideScheduleForPublishedSocial =
    isPublishedDenmarkChip &&
    socialMatchForActiveChip != null &&
    rawSavedScheduleMs == null;

  const readonlySchedulePublishedSocial =
    isPublishedDenmarkChip &&
    socialMatchForActiveChip != null &&
    rawSavedScheduleMs != null;

  const draftInSocialsScheduleCustomOnly =
    socialMatchForActiveChip != null && !isPublishedDenmarkChip;

  const activeDraftChipHasScheduledTime = useMemo(() => {
    if (isPublishedDenmarkChip) return false;
    const raw = currentArticleByChip?.schedule;
    if (raw == null || raw === 0 || Number(raw) === 0) return false;
    return isEpochTimestamp(Number(raw));
  }, [isPublishedDenmarkChip, currentArticleByChip?.schedule]);

  const scheduleControlsUnlocked = useMemo(
    () =>
      readonlySchedulePublishedSocial
        ? true
        : activeDraftChipHasScheduledTime || scheduleSectionUserEnabled,
    [
      readonlySchedulePublishedSocial,
      activeDraftChipHasScheduledTime,
      scheduleSectionUserEnabled,
    ]
  );

  const displayScheduleType =
    currentArticleByChip?.schedule_type ?? fallbackScheduleType ?? "DYNAMIC";
  const displayScheduleAfter =
    displayScheduleType === "DYNAMIC"
      ? (currentArticleByChip?.schedule_after ?? 30)
      : undefined;
  const isCustomSchedule = displayScheduleType === "CUSTOM";

  const showCustomScheduleOnly = true;
  const effectiveScheduleType = showCustomScheduleOnly
    ? "CUSTOM"
    : displayScheduleType;

  const [customScheduleTimestamp, setCustomScheduleTimestamp] = useState<
    number | undefined
  >(undefined);

  const initialDateTimePickerTimestampRef = useRef<number | null>(null);

  useEffect(() => {
    initialDateTimePickerTimestampRef.current = null;
    setScheduleSectionUserEnabled(false);
  }, [selectedChip]);

  useEffect(() => {
    if (selectedChip == null) return;

    if (readonlySchedulePublishedSocial && rawSavedScheduleMs != null) {
      setCustomScheduleTimestamp(rawSavedScheduleMs);
      return;
    }

    if (draftInSocialsScheduleCustomOnly) {
      if (rawSavedScheduleMs != null) {
        setCustomScheduleTimestamp(rawSavedScheduleMs);
      } else {
        const article = selectedArticle.find(
          (item: any) => Number(item?.section) === Number(selectedChip)
        );
        const raw = article?.schedule;
        if (raw != null && isEpochTimestamp(raw)) {
          setCustomScheduleTimestamp(raw);
        } else {
          setCustomScheduleTimestamp(Date.now() + 15 * 60 * 1000);
        }
      }
      return;
    }

    if (!isCustomSchedule) return;
    const article = selectedArticle.find(
      (item: any) => Number(item?.section) === Number(selectedChip)
    );
    const raw = article?.schedule;
    if (raw != null && isEpochTimestamp(raw)) {
      setCustomScheduleTimestamp(raw);
    } else {
      setCustomScheduleTimestamp(Date.now() + 15 * 60 * 1000);
    }
  }, [
    selectedChip,
    isCustomSchedule,
    readonlySchedulePublishedSocial,
    rawSavedScheduleMs,
    draftInSocialsScheduleCustomOnly,
    selectedArticle,
  ]);

  useEffect(() => {
    if (selectedChip == null || !draftInSocialsScheduleCustomOnly) return;
    if (!currentArticleByChip) return;
    if (currentArticleByChip.schedule_type === "CUSTOM") return;
    onUpdateArticleByChip({
      schedule_type: "CUSTOM",
      schedule_after: undefined,
    });
  }, [
    selectedChip,
    draftInSocialsScheduleCustomOnly,
    currentArticleByChip,
    onUpdateArticleByChip,
  ]);

  const applyScheduleOnToggleOn = () => {
    if (selectedChip == null || readonlySchedulePublishedSocial) return;
    let ms =
      rawSavedScheduleMs ??
      customScheduleTimestamp ??
      Date.now() + 15 * 60 * 1000;
    ms = Number(ms);
    if (!Number.isFinite(ms) || ms <= Date.now()) {
      ms = Date.now() + 15 * 60 * 1000;
    }
    setCustomScheduleTimestamp(ms);
    initialDateTimePickerTimestampRef.current = ms;
    onUpdateArticleByChip({
      schedule_type: "CUSTOM",
      schedule: ms,
      schedule_after: undefined,
    });
  };

  if (hideScheduleForPublishedSocial) return null;

  return (
    <div className="mb-4">
      {readonlySchedulePublishedSocial ? (
        <>
          <div className="d-flex align-items-center gap-2 mb-3">
            <i className="bi bi-clock fs-5 text-secondary"></i>
            <h6 className="mb-0 text-uppercase fw-semibold">Schedule</h6>
          </div>
          <div className="d-flex align-items-center rounded px-3 py-2">
            <UnifiedDateTimePicker
              mode="datetime"
              disabled
              onChange={() => {}}
              value={rawSavedScheduleMs}
              outputFormat="timestamp"
              timeAdded={15}
            />
          </div>
        </>
      ) : (
        <>
          <div className="d-flex align-items-center flex-wrap  mb-2">
            <div className="d-flex align-items-center gap-2">
              <i className="bi bi-clock fs-5 text-secondary"></i>
              <h6 className="mb-0 text-uppercase fw-semibold">Schedule</h6>
            </div>
            <div className="form-check form-switch mb-0 d-flex align-items-center">
              <input
                className="form-check-input m-0"
                type="checkbox"
                role="switch"
                id="denmarkScheduleSectionToggle"
                aria-label="Enable scheduling"
                checked={scheduleControlsUnlocked}
                disabled={Boolean(disabled)}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setScheduleSectionUserEnabled(checked);
                  if (checked) {
                    applyScheduleOnToggleOn();
                  } else {
                    initialDateTimePickerTimestampRef.current = null;
                    onUpdateArticleByChip({
                      schedule: 0,
                      schedule_after: undefined,
                      schedule_type: "CUSTOM",
                    });
                  }
                }}
                style={{
                  cursor: disabled ? "not-allowed" : "pointer",
                }}
              />
            </div>
          </div>
          <div
            className={`d-flex align-items-center flex-nowrap gap-3 ${
              !scheduleControlsUnlocked ? "opacity-50" : ""
            }`}
            style={
              !scheduleControlsUnlocked
                ? { pointerEvents: "none" as const }
                : undefined
            }
          >
            {effectiveScheduleType === "DYNAMIC" ? (
              <div className="d-flex align-items-center gap-2 rounded px-3 py-2 bg-light border shadow-sm">
                {[
                  { label: "30 MIN", value: 30 },
                  { label: "1 HR", value: 60 },
                  { label: "2 HR", value: 120 },
                ].map(({ label, value }) => (
                  <button
                    key={label}
                    type="button"
                    className={`btn btn-sm fw-medium ${
                      displayScheduleAfter === value
                        ? "btn-primary"
                        : "btn-outline-secondary"
                    }`}
                    disabled={
                      readonlySchedulePublishedSocial ||
                      !scheduleControlsUnlocked
                    }
                    onClick={() =>
                      onUpdateArticleByChip({
                        schedule_type: "DYNAMIC",
                        schedule_after: value,
                        schedule: undefined,
                      })
                    }
                    style={{ padding: "6px 14px" }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            ) : (
              <div className="d-flex align-items-center rounded px-3 py-2">
                <UnifiedDateTimePicker
                  mode="datetime"
                  disabled={
                    readonlySchedulePublishedSocial || !scheduleControlsUnlocked
                  }
                  onChange={(timestamp: number) => {
                    if (readonlySchedulePublishedSocial) return;
                    if (!scheduleControlsUnlocked) return;
                    if (timestamp <= Date.now()) return;

                    if (initialDateTimePickerTimestampRef.current === null) {
                      initialDateTimePickerTimestampRef.current = timestamp;
                      return;
                    }

                    const currentSchedule = currentArticleByChip?.schedule;
                    if (
                      currentSchedule != null &&
                      currentSchedule === timestamp
                    ) {
                      return;
                    }
                    setCustomScheduleTimestamp(timestamp);
                    onUpdateArticleByChip({
                      schedule_type: "CUSTOM",
                      schedule: timestamp,
                      schedule_after: undefined,
                    });
                  }}
                  value={
                    readonlySchedulePublishedSocial
                      ? rawSavedScheduleMs
                      : (rawSavedScheduleMs ?? customScheduleTimestamp)
                  }
                  outputFormat="timestamp"
                  timeAdded={15}
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DenmarkEditScheduleSection;
